<template>
  <div class="section__body">
      <printTemplate
        v-if="modalActive != null"
        :active.sync="modalActive"
        :formData="medicalGuideFields"
        :patient="patient"
        @cancel="modalActive = null"
      />

    <article class="medicalGuide">
      <section
        v-for="(category, index) in categories"
        :key="`medicalGuide_section_${index}`"
        class="medicalGuide__section"
      >
        <h5 class="medicalGuide__section-title" v-if="category">
          {{ category }}
        </h5>

        <ul class="medicalGuide__field-wrapper">
          <li
            v-for="(field, i) in medicalGuideFields.filter(f => f.category === category)"
            :key="`medicalGuide_field_${index}.${i}`"
            :class="[
              ('flex' in field) ? `medicalGuide__field--${field.flex}` : 'medicalGuide__field--1',
              (!field.readonly) ? 'medicalGuide__field--edit' : ''
            ]"
            class="medicalGuide__field"
          >

            <label class="medicalGuide__label">
              {{ field.placeholder }}
            </label>

            <span
              class="medicalGuide__value"
              v-if="field.readonly && (field.type !== 'date' && field.type !== 'currency')"
            >
              {{ field.value || "&nbsp;" }}
            </span>

            <span
              class="medicalGuide__value"
              v-if="field.readonly && field.type === 'date'"
            >
              {{ formatDate(field.value, field.filter) || '&nbsp;' }}
            </span>

            <span
              class="medicalGuide__value"
              v-if="field.readonly && field.type === 'currency'"
            >
              {{ field.value | TO_MONEY }}
            </span>

            <template v-if="!field.readonly && field.type === 'text'">
              <input
                type="text"
                class="form-input"
                placeholder="Digite aqui"
                v-model="field.value"
                :maxlength="field.maxlength"
              >
            </template>

            <div class="d-flex" v-if="!field.readonly && field.type === 'radio'">
              <label
                v-for="(option, j) in field.options"
                :key="`${field.name}_option${j}`"
                class="form-radio"
              >
                <input
                  type="radio"
                  v-model="field.value"
                  :name="field.name"
                  :value="option.value"
                >
                <i class="form-icon" />
                {{ option.name }}
              </label>
            </div>

            <template v-if="!field.readonly && field.type === 'select'">
              <select v-model="field.value" class="form-select">
                <option
                  v-for="(option, j) in field.options"
                  :key="`${field.name}_option${j}`"
                  :value="option.value"
                >
                  {{ option.name }}
                </option>
              </select>
            </template>
          </li>
        </ul>
      </section>
    </article>

    <footer class="section__footer">
      <dr-submit
        :disabled="!valid"
        :sending="sending"
        text="Salvar"
        @click="submit()"
      />

      <button
        class="btn btn-primary btn-print"
        text="Imprimir"
        @click="modalActive = true"
      >
        Imprimir
      </button>
  </footer>
  </div>
</template>

<script>
import medicalGuideFields from '@/data/formData/medicalGuide'
import drSubmit from '@/components/form/submit'
import printTemplate from '@/components/templates/print/guide'

export default {
  props: ['patient', 'appointment', 'appointmentStarted', 'isMedicalHistoryVisible'],
  components: {
    drSubmit,
    printTemplate
  },
  data () {
    return {
      modalActive: null,
      valid: false,
      sending: false,
      guideId: null,
      agendaId: parseInt(this.$route.params.agendaId) || null,
      doctorOfficeDetails: null,
      appointmentDetails: {},
      medicalGuideFields,
      notes: [],
      formData: {
        date: null,
        note: ''
      }
    }
  },
  computed: {
    categories () {
      return [...new Set(this.medicalGuideFields.map(field => field.category))]
    },

    doctorDetails () {
      return {
        crm: this.$attrs.doctor.crm,
        crm_regional: this.$attrs.doctor.crm_regional,
        doctor_name: this.$attrs.doctor.user.name
      }
    }
  },
  mounted () {
    this.formData.note = this.defaultText
  },
  watch: {
    doctorDetails: {
      immediate: true,
      deep: true,
      handler (next, prev) {
        this.setValues(this.doctorDetails, this.medicalGuideFields)
      }
    },

    doctorOfficeDetails: {
      immediate: false,
      deep: true,
      handler (next, prev) {
        this.setValues(this.doctorOfficeDetails, this.medicalGuideFields)
      }
    },

    appointmentDetails: {
      immediate: false,
      deep: true,
      handler (next, prev) {
        this.setValues(this.appointmentDetails, this.medicalGuideFields)
      }
    },

    medicalGuideFields: {
      immediate: true,
      deep: true,
      handler (fields, prev) {
        const mandatory = fields.filter(f => f.readonly === false)
        this.valid = mandatory.every(f => f.value !== null)
      }
    }
  },
  created () {
    this.init()
  },
  beforeDestroy () {
    this.medicalGuideFields.forEach(field => (field.value = null))
  },
  getDescription: {
    get () {
      return this.formData.note
    },
    set (value) {
      this.formData.note = value
    }
  },
  methods: {
    init () {
      this.fn_SHOW_PROGRESS()

      const appointmentOfficeDetails = this.getAppointmentDetails(this.agendaId)
        .then((res) => {
          this.appointmentDetails = res

          if ('medical_consultation_type' in this.appointmentDetails) {
            delete this.appointmentDetails['medical_consultation_type']
          }

          this.getDoctorOfficeDetails(this.appointmentDetails['doctor_office'].id)
            .then(res => (this.doctorOfficeDetails = this.setPrefix(res, 'office_')))
            .catch(err => console.warn('err\n', err))
        })
        .catch(err => console.warn('err\n', err))

      const getMedicalGuideDetails = this.getMedicalGuideDetails(this.agendaId)
        .then((res) => {
          if (res) {
            this.guideId = res.id
            delete res.id
            this.setValues(res, this.medicalGuideFields)
          }
        })
        .catch((err) => {
          return err
        })

      Promise.all([appointmentOfficeDetails, getMedicalGuideDetails])
        .finally(() => this.fn_HIDE_PROGRESS())
    },

    async getAppointmentDetails (id) {
      // TODO Add patient name to route
      const { data } = await this.$http.get(`${this.fn_IS_SECRETARY() ? `/secretary/doctor/${this.$route.params.doctorId}` : '/doctor'}/agenda/${id}`, this.$store.getters.getAuthHeader) // a rota para role_secretary precisará ser conferida aqui
      const name = await this.$http.get(`/doctor/agenda?filterAnd[a.id]=${id}`, this.$store.getters.getAuthHeader)

      data.patientName = name.data.data[0].patientName
      const idOffice = data['doctor_office'].id
      const office = await this.$http.get(`/doctor/doctor_office/${idOffice}`, this.$store.getters.getAuthHeader)

      if ('special_patient_health_insurance' in data) {
        data['special_patient_health_insurance_wallet_number'] = data['special_patient_health_insurance']['wallet_number']
        data['special_patient_health_insurance_wallet_validity'] = data['special_patient_health_insurance']['wallet_validity']
        data['special_patient_name'] = data['patientName']
        data['special_patient_national_health_card'] = data['special_patient']['national_health_card']
        data['medical_consultation_date'] = data['medical_consultation_finish']
        data['procedure_value'] = name.data.data[0].value
        data['doctor_office_name'] = office.data.name
        data['medical_consultation_type'] = data['medical_consultation_type'] === 'CONSULTA' ? 1 : 2
      }
      return data || null
    },

    async getDoctorOfficeDetails (id) {
      const { data } = await this.$http.get(`/doctor/doctor_office/${id}`, this.$store.getters.getAuthHeader)
      return data || null
    },

    async getMedicalGuideDetails (id) {
      const { data } = await this.$http.get(`${this.fn_IS_SECRETARY() ? '/secretary' : ''}/agenda/${id}/medical_consultation_guide`, this.$store.getters.getAuthHeader) // a rota para role_secretary precisará ser conferida aqui
      return data || null
    },

    setPrefix (obj, prefix) {
      const newObj = {}
      Object.keys(obj).forEach(key => (newObj[`${prefix}${key}`] = obj[key]))
      return newObj
    },

    setValues (from, to) {
      Object.keys(from).forEach((key) => {
        const index = to.findIndex(field => field.name === key)
        index >= 0 && (to[index].value = from[key])
      })
    },

    formatDate (date, format) {
      if (date) {
        return this.$moment(date).format(format)
      }
      return ''
    },

    submit () {
      const url = `${this.fn_IS_SECRETARY() ? '/secretary' : ''}/agenda/${this.agendaId}/medical_consultation_guide` // a rota para role_secretary precisará ser conferida aqui
      const mandatory = this.medicalGuideFields
      const method = this.guideId ? 'put' : 'post'
      const headers = this.$store.getters.getAuthHeader.headers
      const fields = {}
      // const agenda = this.appointment.id
      // const date = this.formData.date
      // const note = this.formData.note

      mandatory.forEach((f) => {
        fields[this.fn_TO_CAMELCASE(f.name)] = f.value
      })

      // true = 1, false = 0
      fields.newbornCare = fields.newbornCare ? '1' : '0'

      const data = { medical_consultation_guide: fields }
      this.$http({
        url,
        method,
        headers,
        data
      })
        .then((res) => {
          this.$store.dispatch('setModalAlert', {
            title: 'Sucesso',
            body: 'Guia salva com sucesso.',
            size: 'md',
            active: true
          })
          this.formData.date = null
          this.formData.note = this.defaultText
          this.sent = true
          this.sending = false
          this.fn_HIDE_PROGRESS()
          this.init()
        })
        .catch((err) => {
          console.log('err\n', err)
          this.$store.dispatch('setModalAlert', {
            title: 'Erro',
            body: 'Ocorreu um erro.',
            size: 'md',
            active: true
          })
        })
    }
  }
}
</script>
